import { useId, type HTMLAttributes, type ReactNode } from 'react';
import { Heading, type HeadingProps } from '../../atoms/Typography/Heading';
import { Text } from '../../atoms/Typography/Text';
import './Section.css';

export interface SectionProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Section title — rendered as `<h2>` (or `<h3>` for nested sections via `titleLevel`). */
  title?: ReactNode;
  /** Optional muted sentence under the title. */
  description?: ReactNode;
  /** Optional action slot, right-aligned next to the title. */
  actions?: ReactNode;
  /** Heading level of the title. Defaults to 2; use 3 only for a Section nested inside another Section. */
  titleLevel?: 2 | 3;
  /** Extra props for the title heading (data-testid, className…). */
  headingProps?: Omit<HeadingProps, 'level' | 'children' | 'id'>;
  children?: ReactNode;
  className?: string;
}

/**
 * Section — a titled content block (`<section aria-labelledby>`).
 * Title → content spacing is var(--spacing-4); sections are separated by the page's Stack (gap 8).
 * Without a title, pass `aria-label` (or `aria-labelledby`) to keep the region named.
 */
export function Section({
  title,
  description,
  actions,
  titleLevel = 2,
  headingProps,
  className = '',
  children,
  ...rest
}: SectionProps) {
  const autoId = useId();
  const headingId = `rg-section-${autoId.replace(/:/g, '')}`;
  const hasTitle = title != null && title !== '';
  const hasHead = hasTitle || actions != null || (description != null && description !== '');

  return (
    <section
      className={['rg-section', className].filter(Boolean).join(' ')}
      aria-labelledby={hasTitle ? headingId : undefined}
      {...rest}
    >
      {hasHead && (
        <div className="rg-section__head">
          <div className="rg-section__titles">
            {hasTitle && (
              <Heading
                level={titleLevel}
                {...headingProps}
                id={headingId}
                className={['rg-section__title', headingProps?.className ?? ''].filter(Boolean).join(' ')}
              >
                {title}
              </Heading>
            )}
            {description != null && description !== '' && (
              <Text variant="muted" className="rg-section__description">
                {description}
              </Text>
            )}
          </div>
          {actions != null && <div className="rg-section__actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

export default Section;
